import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'localization';
import {
  PrimeReactButton,
  ConfirmDialogAtom,
  confirmDialogAtom,
} from 'design-system/my-lib';
import { Toast } from 'primereact/toast';
import './DangerZoneSection.scss';

const DangerZoneSection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const toastRef = useRef<Toast>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);

  const handleTransferOwnership = () => {
    toastRef.current?.show({
      severity: 'info',
      summary: t('orgSettings.danger.transferOwnership.comingSoon'),
      life: 3000,
    });
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogVisible(false);
    toastRef.current?.show({
      severity: 'info',
      summary: t('orgSettings.danger.deleteOrg.comingSoon'),
      life: 3000,
    });
  };

  const handleDeleteClick = () => {
    confirmDialogAtom({
      variant: 'danger',
      header: t('orgSettings.danger.deleteOrg.confirmTitle'),
      message: t('orgSettings.danger.deleteOrg.confirmMessage'),
      accept: handleDeleteConfirm,
      reject: () => {
        /* dialog dismissal handled via onHide */
      },
      acceptLabel: t('orgSettings.danger.deleteOrg.confirmAccept'),
      rejectLabel: t('common.actions.cancel'),
    });
  };

  return (
    <div className="danger-zone-section">
      <Toast ref={toastRef} />
      <ConfirmDialogAtom
        variant="danger"
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
      />

      <header className="danger-zone-section__header">
        <h2 className="danger-zone-section__title">{t('orgSettings.danger.title')}</h2>
        <p className="danger-zone-section__subtitle">{t('orgSettings.danger.subtitle')}</p>
      </header>

      <div className="danger-zone-section__actions">
        <div className="danger-zone-section__action-row">
          <div className="danger-zone-section__action-info">
            <strong>{t('orgSettings.danger.renameOrg.label')}</strong>
            <p>{t('orgSettings.danger.renameOrg.description')}</p>
          </div>
          <PrimeReactButton
            label={t('orgSettings.danger.renameOrg.button')}
            className="p-button-outlined"
            onClick={() => navigate(orgId ? `/${orgId}/settings/general` : '/settings')}
            aria-label={t('orgSettings.danger.renameOrg.button')}
          />
        </div>

        <div className="danger-zone-section__action-row">
          <div className="danger-zone-section__action-info">
            <strong>{t('orgSettings.danger.transferOwnership.label')}</strong>
            <p>{t('orgSettings.danger.transferOwnership.description')}</p>
          </div>
          <PrimeReactButton
            label={t('orgSettings.danger.transferOwnership.button')}
            className="p-button-outlined"
            onClick={handleTransferOwnership}
            aria-label={t('orgSettings.danger.transferOwnership.button')}
          />
        </div>

        <div className="danger-zone-section__action-row danger-zone-section__action-row--destructive">
          <div className="danger-zone-section__action-info">
            <strong>{t('orgSettings.danger.deleteOrg.label')}</strong>
            <p>{t('orgSettings.danger.deleteOrg.description')}</p>
          </div>
          <PrimeReactButton
            label={t('orgSettings.danger.deleteOrg.button')}
            className="p-button-danger"
            onClick={handleDeleteClick}
            aria-label={t('orgSettings.danger.deleteOrg.button')}
          />
        </div>
      </div>
    </div>
  );
};

export default DangerZoneSection;
